import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function AdminBillingReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) { setStatus("failed"); return; }
        let cancel = false;

        const poll = async () => {
            if (cancel) return;
            if (attempts.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
            attempts.current += 1;
            try {
                const r = await api.get(`/admin/billing/status/${sessionId}`);
                if (r.data.payment_status === "paid") { setStatus("paid"); return; }
                setTimeout(poll, POLL_INTERVAL);
            } catch {
                setStatus("failed");
            }
        };
        poll();
        return () => { cancel = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-ink px-6 pt-20 grain" data-testid="billing-return-page">
            <div className="max-w-lg w-full">
                {status === "polling" && (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-10 text-center" data-testid="billing-polling">
                        <Clock size={56} className="text-gold mx-auto mb-4 animate-pulse" strokeWidth={1} />
                        <h1 className="font-serif text-3xl text-white">Verifica pagamento...</h1>
                        <p className="text-gray-400 mt-3 text-sm">Stiamo registrando il saldo commissioni. Non chiudere questa pagina.</p>
                        <div className="mt-6 w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                )}
                {status === "paid" && (
                    <div className="bg-ink-surface border border-emerald-500/30 rounded-sm p-10 text-center" data-testid="billing-success">
                        <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-4" strokeWidth={1} />
                        <h1 className="font-serif text-4xl text-white">Commissione saldata</h1>
                        <p className="text-gray-400 mt-3">
                            Il ristorante è stato <strong className="text-emerald-400">sbloccato</strong>.
                            Puoi ricevere nuove prenotazioni dal sito.
                        </p>
                        <button onClick={() => navigate("/admin/billing")}
                            className="mt-8 bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light"
                            data-testid="go-billing-btn">
                            Torna alla fatturazione
                        </button>
                    </div>
                )}
                {(status === "failed" || status === "timeout") && (
                    <div className="bg-ink-surface border border-red-500/30 rounded-sm p-10 text-center" data-testid="billing-failed">
                        <XCircle size={56} className="text-red-400 mx-auto mb-4" strokeWidth={1} />
                        <h1 className="font-serif text-3xl text-white">{status === "timeout" ? "Verifica in corso" : "Pagamento non riuscito"}</h1>
                        <p className="text-gray-400 mt-3 text-sm">
                            {status === "timeout"
                                ? "La verifica richiede qualche istante. Controlla tra poco nella pagina fatturazione."
                                : "Non siamo riusciti a registrare il pagamento. Riprova dalla pagina fatturazione."}
                        </p>
                        <Link to="/admin/billing" className="inline-block mt-6 text-gold underline underline-offset-2 text-sm">Vai alla fatturazione</Link>
                    </div>
                )}
            </div>
        </div>
    );
}
